import React from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import { get, post } from '../../utils/api';

const enc = (s) => encodeURIComponent(String(s));

/**
 * Invite an event's PAST sponsors to take part again. Audience is the people who bought this event's
 * mapped product in the chosen date range (default: the two years before the event). Test-first, then
 * a confirm-count guard before the real send so a stale screen can't fire at the wrong audience.
 */
export default function SponsorshipRequestDialog({ open, onClose, event }) {
  const eventKey = event?.event_key;
  const [range, setRange] = React.useState({ from: '', to: '' });
  const [aud, setAud] = React.useState(null); // { product_mapped, product, total, with_email, without_email, sponsors[] }
  const [audLoading, setAudLoading] = React.useState(false);
  const [showNames, setShowNames] = React.useState(false);
  const [form, setForm] = React.useState({ subject: '', body: '', participate_url: '', participate_label: '', image_url: '', theme: 'maroon' });
  const [themes, setThemes] = React.useState([]);
  const [busy, setBusy] = React.useState('');
  const [msg, setMsg] = React.useState(null);
  const [testEmail, setTestEmail] = React.useState('');
  const [excluded, setExcluded] = React.useState(() => new Set()); // emails removed from THIS send only

  const loadAudience = React.useCallback(async (from, to) => {
    if (!eventKey) return;
    setAudLoading(true);
    try {
      // withEmails=1 so each name chip carries its email — needed to remove specific people from this send.
      const range = from && to ? `&from=${enc(from)}&to=${enc(to)}` : '';
      const d = await get(`/api/manage/calendar-events/${enc(eventKey)}/sponsors?withEmails=1${range}`);
      setAud(d);
      setExcluded(new Set()); // a fresh audience clears any per-send removals
      if (!from || !to) setRange({ from: d.from, to: d.to });
    } catch (e) { setMsg({ severity: 'error', text: e?.message || 'Failed to load sponsors' }); }
    finally { setAudLoading(false); }
  }, [eventKey]);

  // On open: load the default audience + prefill the email from the server's draft.
  React.useEffect(() => {
    if (!open || !eventKey) return;
    setMsg(null); setShowNames(false);
    loadAudience();
    get(`/api/manage/calendar-events/${enc(eventKey)}/sponsorship-request/preview`)
      .then((d) => {
        setForm({
          subject: d.defaults.subject, body: d.defaults.body,
          participate_url: d.defaults.participate_url, participate_label: d.defaults.participate_label,
          image_url: d.defaults.image_url, theme: d.defaults.theme || 'maroon',
        });
        setThemes(Array.isArray(d.themes) ? d.themes : []);
        if (d.requester_email) setTestEmail(d.requester_email); // prefill "send test to" with my address
      })
      .catch((e) => setMsg({ severity: 'error', text: e?.message || 'Failed to load draft' }));
  }, [open, eventKey, loadAudience]);

  // Preview the CURRENT edits in a new tab. Uses a Blob URL (not about:blank + document.write) so the
  // page has a real origin and the product image loads under the admin CSP.
  const previewEmail = async () => {
    setBusy('preview'); setMsg(null);
    try {
      const r = await post(`/api/manage/calendar-events/${enc(eventKey)}/sponsorship-request/send`,
        { mode: 'preview', ...form });
      const url = URL.createObjectURL(new Blob([r.html || ''], { type: 'text/html' }));
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { setMsg({ severity: 'error', text: e?.message || 'Preview failed' }); }
    finally { setBusy(''); }
  };

  const sendTest = async () => {
    setBusy('test'); setMsg(null);
    try {
      const r = await post(`/api/manage/calendar-events/${enc(eventKey)}/sponsorship-request/send`,
        { mode: 'test', email: testEmail || undefined, ...form });
      setMsg({ severity: 'success', text: `Test sent to ${r.to}.` });
    } catch (e) { setMsg({ severity: 'error', text: e?.message || 'Test failed' }); }
    finally { setBusy(''); }
  };

  // Emailable after the staffer's per-send removals — this is what the server will actually send to,
  // so it's both the displayed count and the confirm_count the server checks.
  const emailableCount = Math.max(0, (aud?.emailable ?? aud?.with_email ?? 0) - excluded.size);

  const sendAll = async () => {
    const n = emailableCount;
    if (!window.confirm(`Send this to ${n} past sponsor${n === 1 ? '' : 's'} of ${aud?.product?.name || 'this event'}? This cannot be undone.`)) return;
    setBusy('send'); setMsg(null);
    try {
      const r = await post(`/api/manage/calendar-events/${enc(eventKey)}/sponsorship-request/send`,
        { mode: 'send', from: range.from, to: range.to, confirm_count: n, exclude: [...excluded], ...form });
      setMsg({ severity: 'success', text: `Sent to ${r.ok} of ${r.total}.${r.failures?.length ? ` ${r.failures.length} failed.` : ''}` });
    } catch (e) {
      const cur = e?.data?.current_count;
      setMsg({ severity: 'error', text: e?.message || 'Send failed' });
      if (cur != null) loadAudience(range.from, range.to);
    } finally { setBusy(''); }
  };

  const noProduct = aud && aud.product_mapped === false;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        Sponsorship Request
        <Typography variant="body2" color="text.secondary">{event?.title}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        {/* AUDIENCE — who this reaches */}
        <Box sx={{ p: 1.5, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="flex-end" flexWrap="wrap" useFlexGap>
            <TextField label="Sponsors from" type="date" size="small" InputLabelProps={{ shrink: true }}
              value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} />
            <TextField label="to" type="date" size="small" InputLabelProps={{ shrink: true }}
              value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} />
            <Button size="small" variant="outlined" disabled={audLoading} onClick={() => loadAudience(range.from, range.to)}>
              {audLoading ? <CircularProgress size={16} /> : 'Update'}
            </Button>
          </Stack>

          {noProduct ? (
            <Alert severity="warning" sx={{ mt: 1.5, py: 0 }}>
              This event has no mapped product, so it has no past sponsors. Map a product first.
            </Alert>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
              <PeopleAltOutlinedIcon fontSize="small" color="action" />
              <Typography variant="body2">
                <strong>{aud ? emailableCount : '—'}</strong> will receive this
                {excluded.size ? ` · ${excluded.size} removed` : ''}
                {aud?.opted_out ? ` · ${aud.opted_out} unsubscribed` : ''}
                {aud?.without_email ? ` · ${aud.without_email} without email (can’t reach)` : ''}
              </Typography>
              {aud?.emailable > 0 && (
                <Button size="small" onClick={() => setShowNames((s) => !s)}>{showNames ? 'Hide names' : 'View names'}</Button>
              )}
            </Stack>
          )}
          <Collapse in={showNames && !!aud?.sponsors?.length}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Click ✕ to remove someone from this send. Removed here only — they are not unsubscribed.
            </Typography>
            <Box sx={{ mt: 0.5, maxHeight: 160, overflow: 'auto', display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {(aud?.sponsors || []).map((s, i) => {
                const em = String(s.email || '').toLowerCase();
                const isOut = em && excluded.has(em);
                return (
                  <Chip key={s.email || i} size="small" label={s.name} variant="outlined"
                    onDelete={em && !isOut ? () => setExcluded((prev) => new Set(prev).add(em)) : undefined}
                    onClick={isOut ? () => setExcluded((prev) => { const n = new Set(prev); n.delete(em); return n; }) : undefined}
                    sx={isOut ? { opacity: 0.5, textDecoration: 'line-through', cursor: 'pointer' } : undefined} />
                );
              })}
            </Box>
          </Collapse>
        </Box>

        {/* MESSAGE — a personal appeal, editable */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Message</Typography>
          {themes.length > 0 && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary">Theme</Typography>
              <ToggleButtonGroup exclusive size="small" value={form.theme}
                onChange={(_e, v) => { if (v) setForm((f) => ({ ...f, theme: v })); }}>
                {themes.map((t) => {
                  const selected = form.theme === t.key;
                  return (
                    <ToggleButton key={t.key} value={t.key} sx={{ textTransform: 'none', px: 1, py: 0.4, gap: 0.6 }}>
                      <Box sx={{ width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
                        background: `linear-gradient(135deg, ${t.primary} 55%, ${t.accent} 55%)`,
                        border: '1px solid rgba(0,0,0,.2)' }} />
                      <Typography variant="caption" sx={{ fontWeight: selected ? 700 : 500 }}>{t.label}</Typography>
                      {selected && <CheckIcon sx={{ fontSize: 15 }} />}
                    </ToggleButton>
                  );
                })}
              </ToggleButtonGroup>
            </Stack>
          )}
        </Stack>
        <Stack spacing={1.5}>
          <TextField label="Subject" size="small" fullWidth value={form.subject}
            onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} />
          <TextField label="Body" size="small" fullWidth multiline minRows={7} value={form.body}
            onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
            helperText="Personal note — use {FirstName} to greet each devotee. Keep it short." />
          <Stack direction="row" spacing={1.5}>
            <TextField label="Button label" size="small" sx={{ width: 190 }} value={form.participate_label}
              onChange={(e) => setForm((f) => ({ ...f, participate_label: e.target.value }))} />
            <TextField label="Button link" size="small" fullWidth value={form.participate_url}
              onChange={(e) => setForm((f) => ({ ...f, participate_url: e.target.value }))} />
          </Stack>
          {form.image_url ? (
            <Typography variant="caption" color="text.secondary">Image: the mapped product photo is shown at the top.</Typography>
          ) : null}
        </Stack>

        <Divider sx={{ my: 2 }} />
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button variant="outlined" onClick={previewEmail} disabled={!!busy || !form.subject || !form.body}>
            {busy === 'preview' ? 'Opening…' : 'Preview'}
          </Button>
          <TextField label="Send test to" size="small" placeholder="me@icct…" value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)} sx={{ width: 220 }} />
          <Button variant="outlined" onClick={sendTest} disabled={!!busy || !form.subject || !form.body}>
            {busy === 'test' ? 'Sending…' : 'Send test'}
          </Button>
        </Stack>

        {msg ? <Alert severity={msg.severity} sx={{ mt: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert> : null}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" color="primary" startIcon={<SendOutlinedIcon />}
          disabled={!!busy || noProduct || !emailableCount || !form.subject || !form.body}
          onClick={sendAll}>
          {busy === 'send' ? 'Sending…' : `Send to ${emailableCount} sponsors`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
